export class StringUtils {
  static getInitialsName(name: string) {
    if (!name) {
      return "";
    }
    const initialsReg = name.match(/\b\w/g) || [];
    const initials = ((initialsReg.shift() || '') + (initialsReg.pop() || '')).toUpperCase();
    return initials;
  }

  static onlyNumbers(value) {
    if (value && (typeof value === 'string' || value instanceof String)) {
      return value.replace(/\D/g, '');
    }
    return value;
  }

  static fixCpfNumber(cpf) {
    if (cpf) {
      cpf = cpf.toString().toUpperCase();
      const clean = cpf.replace(/[.\-/\s]/g, '');

      if (/^[A-Z0-9]+$/.test(clean) && /[A-Z]/.test(clean)) {
        return clean.padStart(14, '0').slice(-14);
      }

      if (clean.length > 11) {
        return clean.padStart(14, '0');
      } else if (clean.length < 11) {
        return clean.padStart(11, '0');
      }

      return clean;
    }
    return cpf;
  }

  static getFormattedTimeElapsed(timeElapsed: number): string {
    let result = '';
    timeElapsed = timeElapsed / 1000;
    const days = Math.floor(timeElapsed / (60 * 60 * 24));
    if (days > 0) {
      result += days + 'd';
      timeElapsed -= days * 60 * 60 * 24;
    }
    const hours = Math.floor(timeElapsed / (60 * 60));
    if (hours > 0 || result.length > 0) {
      result += (hours < 10 ? '0' : '') + hours + 'h';
      timeElapsed -= hours * 60 * 60;
    }
    const minutes = Math.floor(timeElapsed / (60));
    if (minutes > 0 || result.length > 0) {
      result += (minutes < 10 ? '0' : '') + minutes + 'm';
      timeElapsed -= minutes * 60;
    }

    result += (timeElapsed < 10 ? '0' : '') + Math.floor(timeElapsed) + 's';

    return result;
  }

  static getCompactFormattedTimeElapsed(time: number): string {
    const formatted = StringUtils.getFormattedTimeElapsed(time);
    if (/[dh]/.test(formatted) || formatted.includes('h')) {
      return formatted.replace(/\d{1,2}s$/, '');
    }
    return formatted;
  }


  static maskTelefone(value) {
    value = StringUtils.onlyNumbers(value);
    if (value && value.length === 10) {
      return [
        "(", /\d/, /\d/, ")", " ", /\d/, /\d/, /\d/, /\d/, "-", /\d/, /\d/, /\d/, /\d/
      ];
    }
    return [
      "(", /\d/, /\d/, ")", " ", /\d/, /\d/, /\d/, /\d/, /\d/, "-", /\d/, /\d/, /\d/, /\d/,
    ];
  }

  static toDigits(value?: string | number): string {
    return value != null ? String(value).replace(/\D+/g, '') : '';
  }

  static parsePhoneBr(raw?: string) {
    const digits = StringUtils.toDigits(raw);
    if (!digits) return {};
    const ddi = '55';
    const ddd = digits.slice(0, 2);
    const number = digits.slice(2);
    return {
      commercialPhoneDdi: ddi,
      commercialPhoneDdd: ddd,
      commercialPhoneNumber: number,
      phoneRepresentativeDdd: ddd,
      phoneRepresentativeNumber: number
    };
  }

  static maskZip(cep?: string): string {
    const dz = StringUtils.toDigits(cep);
    if (dz.length === 8) return `${dz.slice(0, 5)}-${dz.slice(5)}`;
    return dz || (cep as any);
  }

  static toIsoDateBrOrIso(value?: Date | string): string | undefined {
    if (!value) return undefined;
    if (value instanceof Date) {
      return isNaN(value.getTime()) ? undefined : value.toISOString().slice(0, 10);
    }
    const s = String(value);
    // DD/MM/YYYY
    const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (m) {
      const day = parseInt(m[1], 10);
      const month = parseInt(m[2], 10);
      const year = parseInt(m[3], 10);
      const d = new Date(year, month - 1, day);
      return isNaN(d.getTime()) ? undefined : d.toISOString().slice(0, 10);
    }
    const d = new Date(s);
    return isNaN(d.getTime()) ? undefined : d.toISOString().slice(0, 10);
  }

  static normalizeCivilState(s?: string): { text?: string; code?: string } {
    if (!s) return { code: undefined, text: undefined };
    const mapEnumToText: Record<string, string> = {
      SINGLE: 'solteiro',
      MARRIED: 'casado',
      DIVORCED: 'divorciado',
      WIDOWER: 'viúvo',
    };
    const t = (mapEnumToText[String(s).toUpperCase()] || s).toString().toLowerCase();
    const normalized =
      t.includes('solte') ? 'solteiro' :
        t.includes('casad') ? 'casado' :
          t.includes('divorci') ? 'divorciado' :
            (t.includes('viúv') || t.includes('viuv')) ? 'viúvo' : undefined;

    const mapTextToCode: Record<string, string> = {
      'solteiro': '2',
      'solteiro(a)': '2',
      'casado': '1',
      'casado(a)': '1',
      'divorciado': '3',
      'divorciado(a)': '3',
      'viúvo': '4',
      'viúvo(a)': '4',
    };
    return {
      text: normalized,
      code: normalized ? mapTextToCode[normalized] : undefined,
    };
  }

  static normalize(s: string): string {
    if (!s) return '';
    return s.toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim();
  }

  /** Indica se a URL é de vídeo (por extensão ou marcador #video). Usado para separar fotos e vídeos em report-item. */
  static isVideoUrl(url: string): boolean {
    if (!url || typeof url !== 'string') return false;
    const lower = url.toLowerCase();
    return lower.includes('.mp4') || lower.includes('.webm') || lower.includes('.mov') || lower.includes('.avi') || lower.includes('.ogv') || lower.includes('#video');
  }

  /** Retorna a URL para uso em src (remove o fragmento #video para o recurso carregar corretamente). */
  static getMediaSrc(url: string): string {
    if (!url || typeof url !== 'string') return url;
    return url.replace(/#video$/i, '');
  }
}
